import { cloneDeep, toInteger } from 'lodash';
import { Layer } from '@/layers/layer';
import { limitMaxImageInfo, loadImageAsync } from '@/utils/file-kit';
import { IExportPlugin, IMediaInfo, IMediaLayerProps, LayerType, PluginType } from '@/version-types';
import mediaLayerBehavior from './behavior';
import { getMediaLayerProps } from './constants/default-media-data';

export class MediaLayer extends Layer {
    constructor(identity: string) {
        super(identity);
    }
    /**
     * 设置视频链接地址
     *
     * @param id - 图层 ID
     * @param data - 数据；`data.url` 封面 URL
     */
    async setPosterUrl(id: string, { url }: { url: string }) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const data = layer.data as IMediaLayerProps;
        data.poster = url;
        this.framework.stage.addOrUpdate(layer);
        this.framework.history.write(layer);
        this.framework.operate.setActiveLayer(id);
    }

    /**
     * 添加媒体图层
     *
     * @param materialId - 图层素材 ID
     * @param url - 媒体链接地址
     * @param info - 媒体信息
     */
    async addMediaLayer(materialId: string, url: string, info: IMediaInfo, byMousePosition = false) {
        let { naturalWidth, naturalHeight } = info;
        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();

        const stageRatio = stageWidth / stageHeight;
        if (!naturalWidth || !naturalHeight) {
            const { width, height } = await loadImageAsync(url);
            naturalWidth = width;
            naturalHeight = height;
        }

        // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
        const limitResult = limitMaxImageInfo(stageWidth, url, naturalWidth, naturalHeight);
        naturalWidth = limitResult.naturalWidth;
        naturalHeight = limitResult.naturalHeight;
        url = limitResult.url;

        const ratio = naturalWidth / naturalHeight;

        let width = 0;
        let height = 0;
        if (stageRatio > ratio) {
            height = toInteger(stageHeight / 2);
            width = toInteger(naturalWidth / (naturalHeight / height));
        } else {
            width = toInteger(stageWidth / 2);
            height = toInteger(naturalHeight / (naturalWidth / width));
        }

        const layer = getMediaLayerProps(
            {
                url,
                width,
                height,
                materialId,
            },
            info,
        );

        if (byMousePosition) {
            this.addByMousePosition(layer);
        } else {
            this.addByDiagonal(layer);
        }
    }

    /**
     * 替换图片为媒体图形
     * @param {string} layerId 图层id
     * @param {string} info 媒体图形信息
     * @return {*}
     */
    replaceImg2Media(layerId: string, materialId: string, mediaInfo: any) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer) {
            return;
        }
        const { top, left, parentId } = oldLayer;
        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();
        const { info, url } = mediaInfo;
        const { naturalWidth, naturalHeight } = info;

        const stageRatio = stageWidth / stageHeight;
        const ratio = naturalWidth / naturalHeight;
        let width = 0;
        let height = 0;
        if (stageRatio > ratio) {
            height = toInteger(stageHeight / 2);
            width = toInteger(naturalWidth / (naturalHeight / height));
        } else {
            width = toInteger(stageWidth / 2);
            height = toInteger(naturalHeight / (naturalWidth / width));
        }

        const layer = getMediaLayerProps(
            {
                url,
                left,
                top,
                width,
                height,
                materialId,
            },
            info,
        );

        layer.id = layerId;
        layer.parentId = parentId;
        this.framework.stage.addOrUpdate(cloneDeep(layer));
        this.framework.history.write(cloneDeep(oldLayer));
        this.framework.operate.setActiveLayer(layer.id);
    }
}

/**
 * 媒体图层插件
 */
export const mediaLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.MEDIA_LAYER,
    createInstance({ identity }) {
        return [{ key: 'MediaLayer', value: new MediaLayer(identity) }];
    },
    createBehavior() {
        return mediaLayerBehavior;
    },
};
